/**
 * PageBreakExtension
 *
 * A TipTap extension that adds a page break node to the editor.
 * This forces content below the page break to start on a new page.
 *
 * Features:
 * - Visible in editor as a styled divider with "Page Break" label
 * - In paginated mode, expands to fill remaining page space so content
 *   below visually starts on the next page
 * - Forces page break when printing or exporting to PDF/DOCX
 * - Can be inserted via Insert menu or keyboard shortcut
 *
 * ## How the page-fill works
 *
 * A 200 ms polling loop (outside ProseMirror's transaction cycle to avoid
 * infinite reflow loops with PaginationPlus) measures the distance from
 * each page-break node to the next PaginationPlus ".breaker" decoration
 * via getBoundingClientRect, then sets min-height to fill the gap.
 * The poll auto-stops after 3 stable ticks and restarts on doc changes.
 *
 * ## Known limitations (non-critical — can be improved later)
 *
 * 1. **Zoom / resize**: Changing the editor zoom or resizing the window
 *    shifts breaker positions but doesn't change the document, so the
 *    poll won't restart.  Heights stay correct until the next edit.
 *    Fix: listen for zoom-change / resize events and call ensurePolling.
 *
 * 2. **Hover highlight on blank area**: The expanded page-break inherits
 *    the hover background color across its full height.  Could limit the
 *    hover effect to just the dashed-line area with an inner wrapper.
 *
 * 3. **200 ms latency**: The poll-based approach means there's up to a
 *    200 ms delay before the page break expands after insertion.  Not
 *    noticeable in practice, but a MutationObserver on the pagination
 *    wrapper could make it instant.
 */

import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

export interface PageBreakOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    pageBreak: {
      /**
       * Insert a page break
       */
      setPageBreak: () => ReturnType;
    };
  }
}

export const PageBreak = Node.create<PageBreakOptions>({
  name: "pageBreak",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: "block",

  // Higher priority than HorizontalRule to capture page breaks first
  priority: 1000,

  parseHTML() {
    return [
      {
        tag: 'div[data-type="page-break"]',
      },
      {
        // Support standard page break elements
        tag: "div.page-break",
      },
      {
        // Support hr-based page breaks (from DOCX import)
        tag: 'hr[data-type="page-break"]',
      },
      {
        // Support hr-based page breaks with class
        tag: "hr.page-break",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "page-break",
        class: "page-break",
      }),
      [
        "div",
        { class: "page-break-line" },
      ],
      [
        "div",
        { class: "page-break-label" },
        "Page Break",
      ],
    ];
  },

  addCommands() {
    return {
      setPageBreak:
        () =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Ctrl+Enter to insert page break
      "Mod-Enter": () => this.editor.commands.setPageBreak(),
    };
  },

  addProseMirrorPlugins() {
    const pageBreakFillKey = new PluginKey("pageBreakFill");
    let pollId: ReturnType<typeof setInterval> | null = null;
    let stableCount = 0;
    // How many consecutive poll ticks with no height change before we stop.
    const STABLE_THRESHOLD = 3;
    const MAX_ATTEMPTS = 60; // ~6 s

    /**
     * Measure PaginationPlus's ".breaker" positions and expand each
     * page-break node to fill the remaining page space.
     *
     * Returns true if any height was actually changed.
     */
    const adjustPageBreakHeights = (
      editorDom: HTMLElement,
      source: string
    ): boolean => {
      if (!editorDom.classList.contains("rm-with-pagination")) return false;

      const contentPageBreaks = editorDom.querySelectorAll(
        'div.page-break, div[data-type="page-break"]'
      );
      if (contentPageBreaks.length === 0) return false;

      const breakers = editorDom.querySelectorAll(
        "[data-rm-pagination] .breaker"
      );

      const style = getComputedStyle(editorDom);
      const firstPageHeight = parseFloat(
        style.getPropertyValue("--rm-page-content-first")
      );
      const generalPageHeight = parseFloat(
        style.getPropertyValue("--rm-page-content-general")
      );

      if (breakers.length === 0 && (!firstPageHeight || !generalPageHeight)) {
        return false;
      }

      const zoom = parseFloat(style.getPropertyValue("zoom")) || 1;

      const breakerTops: number[] = [];
      breakers.forEach((b) =>
        breakerTops.push(b.getBoundingClientRect().top)
      );

      let anyChanged = false;

      contentPageBreaks.forEach((pb, idx) => {
        const element = pb as HTMLElement;
        let remaining: number | null = null;
        let strategy = "";

        // Strategy 1 — breaker positions (most accurate)
        if (breakerTops.length > 0) {
          const elementTop = element.getBoundingClientRect().top;
          for (const bt of breakerTops) {
            if (bt > elementTop + 5) {
              remaining = (bt - elementTop) / zoom;
              strategy = "breaker";
              break;
            }
          }
        }

        // Strategy 2 — CSS variable math
        if (remaining === null && firstPageHeight && generalPageHeight) {
          const offsetTop = element.offsetTop;
          if (offsetTop < firstPageHeight) {
            remaining = firstPageHeight - offsetTop;
          } else {
            const posOnPage =
              (offsetTop - firstPageHeight) % generalPageHeight;
            remaining = generalPageHeight - posOnPage;
          }
          strategy = "css-var";
        }

        if (remaining === null) return;
        remaining = Math.max(remaining, 50);

        const currentHeight = parseFloat(element.style.minHeight) || 0;
        if (Math.abs(remaining - currentHeight) > 2) {
          element.style.minHeight = `${remaining}px`;
          element.style.boxSizing = "border-box";
          anyChanged = true;
        }
      });

      return anyChanged;
    };

    /**
     * Start (or reset) a polling loop that runs every 200 ms,
     * independent of ProseMirror transactions.  Stops automatically
     * after heights have been stable for STABLE_THRESHOLD ticks.
     */
    const ensurePolling = (dom: HTMLElement) => {
      if (pollId) {
        // Already running — just reset the stable counter so the loop
        // keeps going long enough to pick up the new change.
        stableCount = 0;
        return;
      }
      stableCount = 0;
      let attempts = 0;
      pollId = setInterval(() => {
        attempts++;
        if (attempts > MAX_ATTEMPTS) {
          clearInterval(pollId!);
          pollId = null;
          return;
        }
        const changed = adjustPageBreakHeights(dom, `poll#${attempts}`);
        if (changed) {
          stableCount = 0;
        } else {
          stableCount++;
          if (stableCount >= STABLE_THRESHOLD) {
            clearInterval(pollId!);
            pollId = null;
          }
        }
      }, 100);
    };

    return [
      new Plugin({
        key: pageBreakFillKey,
        view: () => {
          let initialized = false;
          return {
            update: (view, prevState) => {
              // Only react to actual document changes or first mount —
              // never do DOM measurement here (it forces a layout reflow
              // that creates an infinite loop with PaginationPlus).
              const docChanged = view.state.doc !== prevState.doc;
              if (!initialized || docChanged) {
                initialized = true;
                ensurePolling(view.dom);
              }
            },
            destroy: () => {
              if (pollId) clearInterval(pollId);
            },
          };
        },
      }),
    ];
  },
});

export default PageBreak;
